import { adaptV4Theme, createTheme } from "@mui/material";
import { enUS, svSE } from "@mui/material/locale";
import i18n from "i18next";

import { semantic } from "theme/semantic";
import { Definitions } from "theme/typography";

import { theme } from "..";

const getMuiLocale = () => {
  switch (i18n.language) {
    case "sv":
    case "sv-SE":
      return svSE;
    case "en":
    case "en-GB":
    case "en-US":
      return enUS;
    default:
      return enUS;
  }
};

export const museoSansFont = '"Museo Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

export const muiTheme = createTheme(
  adaptV4Theme({
    overrides: {
      MuiAccordion: {
        root: {
          background: theme.colors.greys.light4,
        },
      },
      MuiAlert: {
        standardSuccess: {
          backgroundColor: theme.colors.redesign.g100,
          color: theme.colors.white,
          "& .MuiAlert-icon": {
            color: theme.colors.white,
          },
        },
        standardError: {
          backgroundColor: theme.colors.redesign.r100,
          color: theme.colors.white,
          "& .MuiAlert-icon": {
            color: theme.colors.white,
          },
        },
        standardWarning: {
          backgroundColor: theme.colors.redesign.y100,
          color: theme.colors.white,
          "& .MuiAlert-icon": {
            color: theme.colors.white,
          },
        },
        standardInfo: {
          backgroundColor: theme.colors.redesign.b100,
          color: theme.colors.white,
          "& .MuiAlert-icon": {
            color: theme.colors.white,
          },
        },
      },

      MuiFormLabel: {
        root: {
          fontSize: 14,
        },
      },
      MuiInputBase: {
        root: {
          fontSize: 14,
        },
        input: {
          "&.MuiInputBase-inputSizeSmall:not(.MuiSelect-multiple)": {
            height: 40,
          },
          "&.MuiInputBase-inputSizeSmall:not(.MuiSelect-select)": {
            padding: "0 14px",
          },
        },
      },
      MuiSelect: {
        select: {
          "&.MuiSelect-outlined.MuiInputBase-inputSizeSmall:not(.MuiSelect-multiple)": {
            minHeight: 40,
            lineHeight: "40px",
            paddingTop: "0",
            paddingBottom: "0",
            display: "flex",
            alignItems: "center",
          },
          "&.MuiSelect-outlined.MuiInputBase-inputSizeSmall.MuiSelect-multiple": {
            height: "auto",
            display: "flex",
            alignItems: "center",
            padding: "10px 14px",
          },
        },
      },
      MuiAutocomplete: {
        option: {
          "&[aria-selected=true]": {
            backgroundColor: theme.colors.redesign.b30,
          },
        },
        paper: {
          boxShadow: "0px 10px 20px rgba(141, 155, 173, 0.3);",
        },
      },
      MuiTabs: {
        indicator: {
          backgroundColor: theme.colors.redesign.b100,
        },
      },
      MuiTab: {
        root: {
          fontSize: 22,
          fontWeight: 500,
          letterSpacing: 0.1,
          textTransform: "none",
          color: theme.colors.redesign.db100,
          "&.Mui-selected": {
            color: theme.colors.redesign.b100,
          },
        },
      },
      MuiPaginationItem: {
        root: {
          fontSize: 14,
          "&.MuiPaginationItem-page": {
            backgroundColor: theme.colors.redesign.db1,
            color: theme.colors.redesign.db100,
            "&.Mui-selected": {
              backgroundColor: theme.colors.redesign.b10,
              border: `1px solid ${theme.colors.redesign.b100}`,
              color: theme.colors.redesign.b100,
            },
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          "&:hover": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.colors.redesign.b100,
            },
          },
          "&.Mui-disabled": {
            backgroundColor: theme.colors.redesign.db5,
            color: theme.colors.redesign.db40,

            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.colors.redesign.db20,
            },
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.colors.redesign.b100,
          },
        },
        notchedOutline: {
          borderColor: theme.colors.redesign.db20,
        },
      },
    },
    palette: {
      primary: {
        main: theme.colors.redesign.b100,
        contrastText: theme.colors.white,
      },
      success: {
        main: theme.colors.redesign.g100,
        contrastText: theme.colors.white,
      },
      error: {
        main: theme.colors.redesign.r100,
        contrastText: theme.colors.white,
      },
      warning: {
        main: theme.colors.redesign.y100,
        contrastText: theme.colors.white,
      },
      custom: {
        accent: theme.colors.redesign.b100,
      },
      text: {
        secondary: theme.colors.redesign.db70,
      },
      divider: theme.colors.redesign.db20,
    },
    props: {
      MuiButtonBase: {
        disableRipple: true,
      },
      MuiStack: {
        useFlexGap: true,
      },
      MuiTypography: {
        variantMapping: {
          body_bold: "p",
          body_thin: "p",
          body_small: "p",
          caption2: "h6",
        },
      },
    },
    semantic,
    shape: {
      borderRadius: 8,
    },
    typography: {
      fontFamily: museoSansFont,
      ...Definitions,
    },
  }),
  getMuiLocale()
);

// Add DataGrid specific overrides
if (muiTheme.components) {
  // @ts-expect-error - MuiDataGrid is from @mui/x-data-grid and not in base MUI theme types
  muiTheme.components.MuiDataGrid = {
    styleOverrides: {
      root: {
        fontFamily: museoSansFont,
        fontSize: 14,
      },
      columnHeader: {
        fontFamily: museoSansFont,
        fontWeight: 600,
      },
      cell: {
        fontFamily: museoSansFont,
      },
    },
  };
}
